import { Link, useLocation, useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import { motion } from "motion/react"
import {
  ArrowLeft,
  Phone01,
  Mail01,
  Zap,
  CheckCircle,
  AlertTriangle,
  SlashCircle01,
  ClockFastForward,
  Hand,
  PhoneCall01,
  Car01,
  ChevronRight,
  CalendarDate,
  RefreshCw02,
  SearchRefraction,
} from "@untitledui/icons"
import { useCustomerProfile } from "@/lib/customers/use-customer-profile"
import {
  useCustomerActivity,
  useSetCustomerConsent,
} from "@/lib/api/use-customer-activity"
import type {
  CustomerActivityResponse,
  CustomerEnrollment,
  CustomerEvent,
} from "@/lib/api/customer-activity"
import type { ApiEstimate } from "@/lib/api/types"
import { StatusBadge } from "@/components/estimates/status-badge"
import { CustomerConversation } from "@/components/customers/customer-conversation"
import { formatMoney, formatMoneyExact } from "@/lib/service-lane/baseline-math"
import { cx } from "@/lib/utils/cx"
import { ProtectedRoute } from "@/lib/auth/protected-route"
import { AppLayout } from "@/components/layout/app-layout"
import { ALL_ROLES } from "@/lib/team/roles"

// The Customer Card — everything about one person in a single place: who they
// are, what we've found for them (estimates), what our automation has done
// (the drip + conversation), and the one thing to do next. Built as the hub
// Sam asked for on Jul 10 ("look up the customer, see their communications").

export default function CustomerPage() {
  const { id } = useParams()
  const profile = useCustomerProfile(id ?? null)
  const activityQ = useCustomerActivity(id ?? null)

  return (
    <ProtectedRoute allowedRoles={ALL_ROLES}>
      <AppLayout>
        <div className="min-h-screen bg-zinc-50/40">
          <div className="mx-auto max-w-6xl px-4 md:px-8 py-6 md:py-10">
            <BackLink />
        {profile.isLoading ? (
          <LoadingCard />
        ) : !profile.customer ? (
          <NotFound />
        ) : (
          <Body
            customerId={Number(id)}
            profile={profile}
            activity={activityQ.data}
            activityError={activityQ.isError}
          />
        )}
          </div>
        </div>
      </AppLayout>
    </ProtectedRoute>
  )
}

function BackLink() {
  const navigate = useNavigate()
  const location = useLocation()
  // navigate(-1) is a dead click when the card was opened via a deep link (no
  // in-app history to pop). Fall back to the directory so Back always lands.
  const canGoBack = location.key !== "default"
  return (
    <button
      type="button"
      onClick={() => (canGoBack ? navigate(-1) : navigate("/customers"))}
      className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-zinc-500 hover:text-zinc-900 transition-colors mb-4"
    >
      <ArrowLeft className="size-3.5" aria-hidden />
      Back
    </button>
  )
}

function LoadingCard() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-28 rounded-2xl bg-white border border-zinc-200/70" />
      <div className="h-40 rounded-2xl bg-white border border-zinc-200/70" />
      <div className="h-64 rounded-2xl bg-white border border-zinc-200/70" />
    </div>
  )
}

function NotFound() {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white p-10 text-center">
      {/* Same icon-tile idiom as every other empty state (#67). */}
      <div className="size-11 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-3">
        <SearchRefraction className="size-5 text-zinc-400" aria-hidden />
      </div>
      <p className="text-[15px] font-semibold text-zinc-900">Customer not found</p>
      <p className="text-[13px] text-zinc-500 mt-1">
        We couldn't find any estimates for this customer in your stores.
      </p>
      <Link
        to="/estimates"
        className="inline-flex items-center gap-1.5 mt-4 text-[13px] font-semibold text-zinc-700 hover:text-zinc-900"
      >
        Go to estimates <ChevronRight className="size-3.5" aria-hidden />
      </Link>
    </div>
  )
}

type Profile = ReturnType<typeof useCustomerProfile>

function Body({
  customerId,
  profile,
  activity,
  activityError,
}: {
  customerId: number
  profile: Profile
  activity: CustomerActivityResponse | undefined
  activityError?: boolean
}) {
  const c = profile.customer!
  // Estimate context the follow-up panel needs to give the right "next action":
  // a fresh Pending waiting to be confirmed (repeat customer), the newest
  // estimate's outcome (declined vs no-response), and whether a repair is done
  // (time to ask for a review).
  const allEstimates = profile.vehicles.flatMap((v) => v.estimates)
  const pending = allEstimates.find((e) => e.status === "Pending" || e.status === "Draft") ?? null
  const latest =
    [...allEstimates].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0] ?? null
  const jobCompleted = allEstimates.some((e) => e.status === "Completed")
  return (
    // The record: a dark hero band (the brand's charcoal + emerald language —
    // same family as the dashboard's opportunity panel) carries identity and
    // the money story; below it, three zones — contact rail · information
    // column · the conversation as its own workspace (sticky at xl, last
    // below xl, never wedged between information).
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-5"
    >
      <HeroBand
        profile={profile}
        enrollment={activity?.enrollment}
        consent={activity?.consent}
      />
      <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[280px_minmax(0,1fr)_420px] items-start">
      <div className="lg:sticky lg:top-6">
        <ContactRail profile={profile} />
      </div>
      <div className="space-y-5 min-w-0">
        <FollowUpPanel
          customerId={customerId}
          phone={c.phone}
          activity={activity}
          pending={pending}
          latest={latest}
          jobCompleted={jobCompleted}
        />
        <EstimatesHistory profile={profile} />
      </div>
      <div className="min-w-0 xl:sticky xl:top-6">
        <ConversationSection>
          {activity ? (
            <CustomerConversation
              customerId={customerId}
              events={activity.events}
              consent={activity.consent}
              jobCompleted={jobCompleted}
              customerName={c.name}
            />
          ) : activityError ? (
            // Failed fetch is not "loading forever" — say it broke (#52).
            <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-6 text-center text-[13px] text-amber-900">
              Couldn't load the conversation — check your connection and reload.
            </div>
          ) : (
            <div className="rounded-2xl border border-zinc-200/80 bg-white p-4 space-y-3 animate-pulse">
              <div className="h-10 w-3/5 rounded-2xl bg-zinc-100" />
              <div className="h-10 w-1/2 rounded-2xl bg-zinc-100 ml-auto" />
              <div className="h-10 w-2/3 rounded-2xl bg-zinc-100" />
            </div>
          )}
        </ConversationSection>
      </div>
      </div>
    </motion.div>
  )
}

/* ── Header ─────────────────────────────────────────────── */

// Pills on the dark hero band — the brand's charcoal + emerald language, not
// generic light chips.
const ENROLL_PILL: Record<
  CustomerEnrollment["status"],
  { label: string; cls: string }
> = {
  active: {
    label: "Following up",
    cls: "bg-teal-400/15 text-teal-300 ring-teal-400/30",
  },
  converted: {
    label: "Converted",
    cls: "bg-emerald-400/15 text-emerald-300 ring-emerald-400/30",
  },
  unresponsive: {
    label: "No response",
    cls: "bg-amber-400/15 text-amber-300 ring-amber-400/30",
  },
  stopped: { label: "Do not contact", cls: "bg-white/10 text-zinc-300 ring-white/15" },
  not_started: { label: "Not started", cls: "bg-white/10 text-zinc-400 ring-white/15" },
}

// The dark hero band — the brand's charcoal + emerald voice (same family as
// the dashboard's opportunity panel). Identity on the left, the money story on
// the right, the counter-person's questions (repeat? coming in?) as chips.
function HeroBand({
  profile,
  enrollment,
  consent,
}: {
  profile: Profile
  enrollment: CustomerEnrollment | undefined
  consent: CustomerActivityResponse["consent"] | undefined
}) {
  const c = profile.customer!
  const tel = c.phone ? `tel:${c.phone.replace(/[^\d+]/g, "")}` : null
  const pill = enrollment ? ENROLL_PILL[enrollment.status] : null
  // 'stopped' covers a genuine opt-out (Do Not Contact) AND a softer "handled
  // offline" pause — don't brand the latter as DNC (#51).
  const pillLabel =
    enrollment?.status === "stopped"
      ? consent === "opted_out"
        ? "Do not contact"
        : "Follow-up paused"
      : pill?.label

  const initials = (c.name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()
  const captured = profile.estimates
    .filter((e) => e.status === "Completed")
    .reduce((s, e) => s + (e.avg || 0), 0)
  const upcoming =
    profile.estimates
      .filter((e) => e.status === "Scheduled" && e.scheduledAt)
      .map((e) => new Date(e.scheduledAt!))
      .sort((a, b) => a.getTime() - b.getTime())[0] ?? null
  const isRepeat = profile.counts.total >= 2
  const upcomingYmd = upcoming
    ? `${upcoming.getFullYear()}-${String(upcoming.getMonth() + 1).padStart(2, "0")}-${String(
        upcoming.getDate(),
      ).padStart(2, "0")}`
    : null

  return (
    <header className="rounded-2xl bg-zinc-900 text-white shadow-xl shadow-zinc-900/10 overflow-hidden">
      <div className="px-5 md:px-7 py-5 md:py-6 flex flex-wrap items-center justify-between gap-x-8 gap-y-5">
        <div className="flex items-center gap-4 min-w-0">
          <div className="size-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-emerald-600 grid place-items-center shrink-0 shadow-lg shadow-emerald-500/25">
            <span className="text-[17px] font-bold text-white">{initials || "??"}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              Customer
            </p>
            <h1 className="text-[22px] md:text-[24px] font-bold tracking-tight leading-tight truncate text-white">
              {c.name || "Customer"}
            </h1>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              {pill && (
                <span
                  className={cx(
                    "inline-flex items-center px-2 py-0.5 rounded-md text-[10.5px] font-semibold ring-1 ring-inset",
                    pill.cls,
                  )}
                >
                  {pillLabel}
                </span>
              )}
              {isRepeat && (
                <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-emerald-300">
                  <RefreshCw02 className="size-3 shrink-0" aria-hidden />
                  {profile.counts.total} visits
                </span>
              )}
              {upcoming && (
                <Link
                  to={`/calendar?d=${upcomingYmd}`}
                  className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-sky-300 hover:text-sky-200"
                >
                  <CalendarDate className="size-3 shrink-0" aria-hidden />
                  Booked ·{" "}
                  {upcoming.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </Link>
              )}
              {tel && (
                <a
                  href={tel}
                  className="inline-flex items-center gap-1 text-[11.5px] font-medium text-zinc-400 hover:text-white"
                >
                  <Phone01 className="size-3 shrink-0" aria-hidden />
                  {c.phone}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* The money story — big, tabular, emerald where it counts. */}
        <div className="flex items-stretch gap-6 md:gap-8">
          <BandStat
            label="Found for them"
            value={formatMoneyExact(profile.totalIdentified)}
            tone="white"
          />
          <BandStat
            label="Captured"
            value={captured > 0 ? formatMoneyExact(captured) : "—"}
            tone={captured > 0 ? "emerald" : "dim"}
          />
          <BandStat label="Open" value={String(profile.counts.open)} tone="dim" />
        </div>
      </div>
    </header>
  )
}

function BandStat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone: "white" | "emerald" | "dim"
}) {
  return (
    <div>
      <p
        className={cx(
          "text-[20px] md:text-[22px] font-bold tabular-nums leading-none",
          tone === "emerald"
            ? "text-emerald-300"
            : tone === "white"
              ? "text-white"
              : "text-zinc-400",
        )}
      >
        {value}
      </p>
      <p className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-zinc-500 mt-1.5">
        {label}
      </p>
    </div>
  )
}

// The contact rail — how to reach them, their garage, what we did last time.
// Flat sections split by rules, not nested boxes.
function ContactRail({ profile }: { profile: Profile }) {
  const c = profile.customer!
  const tel = c.phone ? `tel:${c.phone.replace(/[^\d+]/g, "")}` : null
  const since =
    profile.estimates.length > 0
      ? new Date(profile.estimates[profile.estimates.length - 1].createdAt)
      : null
  const lastCompleted = profile.estimates.find((e) => e.status === "Completed")

  return (
    <aside className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm divide-y divide-zinc-100">
      <div className="p-4">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-2.5">
          Contact
        </p>
        <div className="space-y-2">
          {tel ? (
            <a
              href={tel}
              className="flex items-center gap-2 text-[13px] font-medium text-zinc-700 hover:text-zinc-900"
            >
              <Phone01 className="size-3.5 text-zinc-400 shrink-0" aria-hidden />
              {c.phone}
            </a>
          ) : (
            <span className="flex items-center gap-2 text-[13px] text-amber-600">
              <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
              No phone on file
            </span>
          )}
          {c.email && (
            <a
              href={`mailto:${c.email}`}
              className="flex items-center gap-2 text-[13px] font-medium text-zinc-700 hover:text-zinc-900 min-w-0"
            >
              <Mail01 className="size-3.5 text-zinc-400 shrink-0" aria-hidden />
              <span className="truncate">{c.email}</span>
            </a>
          )}
        </div>
      </div>

      {profile.vehicles.length > 0 && (
        <div className="p-4">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-2">
            Garage
          </p>
          <div className="space-y-1.5">
            {profile.vehicles.slice(0, 3).map((v) => (
              <div
                key={v.vin ?? v.label}
                className="flex items-center gap-2 text-[12.5px] text-zinc-700"
              >
                <Car01 className="size-3.5 text-zinc-400 shrink-0" aria-hidden />
                <span className="truncate">{v.label}</span>
                {v.estimates.length > 1 && (
                  <span className="text-[11px] text-zinc-400 shrink-0">
                    ×{v.estimates.length}
                  </span>
                )}
              </div>
            ))}
            {profile.vehicles.length > 3 && (
              <p className="text-[11px] text-zinc-400">
                +{profile.vehicles.length - 3} more
              </p>
            )}
          </div>
        </div>
      )}

      {lastCompleted && (
        <div className="p-4">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-1.5">
            Last visit
          </p>
          <p className="text-[12.5px] text-zinc-700 leading-snug">
            {workSummary(lastCompleted) ?? "Repair completed"}
          </p>
          <p className="text-[11px] text-zinc-400 mt-0.5 tabular-nums">
            {new Date(lastCompleted.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}{" "}
            · {formatMoneyExact(lastCompleted.avg)}
          </p>
        </div>
      )}

      {since && (
        <p className="p-4 text-[11.5px] text-zinc-400 leading-relaxed">
          Customer since{" "}
          {since.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
        </p>
      )}
    </aside>
  )
}

/* ── Follow-up hub (the do-it-for-them panel) ───────────── */

function FollowUpPanel({
  customerId,
  phone,
  activity,
  pending,
  latest,
  jobCompleted,
}: {
  customerId: number
  phone: string | null
  activity: CustomerActivityResponse | undefined
  pending: ApiEstimate | null
  latest: ApiEstimate | null
  jobCompleted: boolean
}) {
  const setConsent = useSetCustomerConsent(customerId)

  if (!activity) {
    return (
      <div className="rounded-2xl border border-zinc-200/80 bg-white p-6 text-center text-[13px] text-zinc-400">
        Loading follow-up…
      </div>
    )
  }

  const { enrollment, consent, events } = activity
  const stopped = consent !== "subscribed"
  const action = nextBestAction(enrollment, events, phone, consent, {
    pending,
    latest,
    jobCompleted,
  })

  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden">
      <div className="px-5 md:px-7 py-4 md:py-5">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="size-4 text-zinc-400" aria-hidden />
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400">
            Marketing Supercharge
          </p>
        </div>

        {/* Do-Not-Contact / handled-offline banner (the anti-spam guardrail) */}
        {stopped && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-3 flex items-start gap-2.5 mb-3">
            {consent === "opted_out" ? (
              <SlashCircle01 className="size-4 text-red-500 shrink-0 mt-0.5" aria-hidden />
            ) : (
              <Hand className="size-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-amber-900 leading-snug">
                {consent === "opted_out"
                  ? "This customer opted out of messages."
                  : "Follow-up is stopped — marked handled offline."}
              </p>
              <p className="text-[12px] text-amber-800/80 mt-0.5 leading-snug">
                No automated texts or emails will go out.
              </p>
            </div>
            {consent !== "opted_out" && (
              <button
                type="button"
                disabled={setConsent.isPending}
                onClick={() => setConsent.mutate({ state: "subscribed" })}
                className="shrink-0 h-8 px-3 rounded-lg bg-white border border-amber-300 text-[12px] font-semibold text-amber-800 hover:bg-amber-50 transition-colors disabled:opacity-50"
              >
                Resume
              </button>
            )}
          </div>
        )}

        {/* No-phone data-quality flag — the drip silently can't run without it */}
        {!phone && !stopped && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-3 flex items-start gap-2.5 mb-3">
            <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" aria-hidden />
            <p className="text-[12.5px] text-amber-900 leading-snug">
              <span className="font-semibold">No phone on file.</span> Automated
              text follow-up can't run — add a number to the estimate to start it.
            </p>
          </div>
        )}

        {/* Progress — only when actively running */}
        {!stopped && phone && enrollment.status !== "not_started" && (
          <div className="mb-3">
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <p className="text-[13px] font-semibold text-zinc-800">
                {dripSummary(events)}
              </p>
              <p
                className="text-[11px] text-zinc-400 tabular-nums shrink-0"
                aria-label={`Step ${Math.min(enrollment.currentStep, enrollment.totalSteps)} of ${enrollment.totalSteps} in the follow-up sequence`}
              >
                {Math.min(enrollment.currentStep, enrollment.totalSteps)} of{" "}
                {enrollment.totalSteps}
              </p>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: enrollment.totalSteps }).map((_, i) => (
                <span
                  key={i}
                  className={cx(
                    "h-1.5 flex-1 rounded-full",
                    i < enrollment.currentStep ? "bg-emerald-400" : "bg-zinc-100",
                  )}
                />
              ))}
            </div>
          </div>
        )}

        {/* The link the texts carry — copy it for a call, or open it to see
            what the customer sees. */}
        {!stopped && enrollment.bookingUrl && (
          <div className="mb-3 flex items-center justify-between gap-2 rounded-xl border border-zinc-200/60 bg-zinc-50/60 px-3.5 py-2.5">
            <p className="text-[12px] text-zinc-600 truncate">
              Their booking link{" "}
              <span className="font-mono text-[11px] text-zinc-400">
                {enrollment.bookingUrl}
              </span>
            </p>
            <div className="flex items-center gap-2.5 shrink-0">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard
                    .writeText(`${window.location.origin}${enrollment.bookingUrl}`)
                    .then(() => toast.success("Booking link copied"))
                    .catch(() => toast.error("Couldn't copy"))
                }}
                className="text-[12px] font-semibold text-zinc-600 hover:text-zinc-900"
              >
                Copy
              </button>
              <a
                href={enrollment.bookingUrl}
                target="_blank"
                rel="noreferrer"
                className="text-[12px] font-semibold text-emerald-700 hover:text-emerald-800"
              >
                Open
              </a>
            </div>
          </div>
        )}

        {/* Next best action — the one thing to do */}
        <div
          className={cx(
            "rounded-xl px-3.5 py-3 flex items-start gap-3",
            action.tone === "good"
              ? "bg-emerald-50/60 border border-emerald-100"
              : action.tone === "warn"
                ? "bg-amber-50/60 border border-amber-100"
                : "bg-zinc-50 border border-zinc-200/60",
          )}
        >
          <div className="mt-0.5 shrink-0">
            {action.tone === "good" ? (
              <CheckCircle className="size-4 text-emerald-600" aria-hidden />
            ) : action.icon === "call" ? (
              <PhoneCall01 className="size-4 text-zinc-500" aria-hidden />
            ) : (
              <ClockFastForward className="size-4 text-zinc-500" aria-hidden />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-0.5">
              Next best action
            </p>
            <p className="text-[13.5px] text-zinc-800 leading-snug">{action.text}</p>
          </div>
          {action.icon === "call" && phone && (
            <a
              href={`tel:${phone.replace(/[^\d+]/g, "")}`}
              className="shrink-0 h-8 px-3 rounded-lg bg-zinc-900 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-black transition-colors"
            >
              <Phone01 className="size-3.5" aria-hidden />
              Call
            </a>
          )}
          {action.link && (
            <Link
              to={action.link.to}
              className="shrink-0 h-8 px-3 rounded-lg bg-zinc-900 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 hover:bg-black transition-colors"
            >
              {action.link.label}
              <ChevronRight className="size-3.5" aria-hidden />
            </Link>
          )}
        </div>

        {/* Stop control — one tap to halt when handled elsewhere */}
        {!stopped && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              disabled={setConsent.isPending}
              onClick={() =>
                setConsent.mutate({ state: "handled_offline" })
              }
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-zinc-200 text-[12px] font-semibold text-zinc-600 hover:bg-zinc-50 transition-colors disabled:opacity-50"
            >
              <Hand className="size-3.5" aria-hidden />
              Handled offline — stop follow-up
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

type Action = {
  text: string
  tone: "good" | "warn" | "neutral"
  icon?: "call"
  link?: { to: string; label: string }
}

function nextBestAction(
  en: CustomerEnrollment,
  events: CustomerEvent[],
  phone: string | null,
  consent: CustomerActivityResponse["consent"],
  ctx: { pending: ApiEstimate | null; latest: ApiEstimate | null; jobCompleted: boolean },
): Action {
  if (consent === "opted_out")
    return { text: "This customer opted out — do not contact them.", tone: "warn" }
  if (consent === "handled_offline" || consent === "paused")
    return { text: "Follow-up is paused. Resume it above if they're back in play.", tone: "neutral" }
  // A fresh estimate waiting on a human OK — the one thing to do, even if an
  // older estimate already converted (repeat customer).
  if (ctx.pending)
    return {
      text: "A new estimate is waiting — confirm it to start automated follow-up.",
      tone: "warn",
      link: { to: `/estimates/${ctx.pending.id}`, label: "Review" },
    }
  if (!phone)
    return { text: "Add a phone number so automated follow-up can start.", tone: "warn" }
  if (en.status === "not_started")
    return { text: "Confirm an estimate to kick off automated follow-up.", tone: "neutral" }
  if (en.status === "converted") {
    // Repair's done → the next move is a review while they're happy (#28).
    if (ctx.jobCompleted)
      return {
        text: "Repair's done — send a quick thank-you and ask for a review below while they're happy.",
        tone: "good",
      }
    return { text: "Booked — nice work. Nothing needed here.", tone: "good" }
  }
  if (en.status === "unresponsive") {
    // A declined estimate isn't "no response" — say so, and offer the call.
    if (ctx.latest?.status === "Declined")
      return {
        text: "They passed on this estimate. If it's worth another look, a quick call is the move.",
        tone: "warn",
        icon: "call",
      }
    return {
      text: `No reply after ${en.totalSteps} touches — a quick call usually lands these.`,
      tone: "warn",
      icon: "call",
    }
  }
  const last = [...events].reverse().find((e) => e.kind !== "delivered")
  // You've texted back → the drip pauses so it won't talk over you (#16).
  if (last?.kind === "agent_reply")
    return {
      text: "You're in the conversation — automated texts are paused so they won't overlap.",
      tone: "neutral",
    }
  if (last?.kind === "replied")
    return { text: "They just replied — send a time to lock it in.", tone: "neutral" }
  if (last?.kind === "opened")
    return {
      text: "They opened your last email but haven't replied — a call might close it.",
      tone: "neutral",
      icon: "call",
    }
  const next = en.nextTouchAt ? relDay(en.nextTouchAt) : null
  return {
    text: next
      ? `On autopilot — next text ${next}. You don't need to do anything.`
      : "On autopilot — we're working this lead.",
    tone: "good",
  }
}

function dripSummary(events: CustomerEvent[]): string {
  const sms = events.filter((e) => e.kind === "sms_sent").length
  const email = events.filter((e) => e.kind === "email_sent").length
  const opened = events.some((e) => e.kind === "opened")
  const replied = events.some((e) => e.kind === "replied")
  const parts: string[] = []
  if (sms) parts.push(`${sms} text${sms > 1 ? "s" : ""}`)
  if (email) parts.push(`${email} email${email > 1 ? "s" : ""}`)
  let s = parts.length ? `Sent ${parts.join(" + ")}` : "Just enrolled"
  if (replied) s += " · they replied"
  else if (opened) s += " · opened, no reply yet"
  return s
}

function relDay(iso: string): string {
  const d = Math.round((new Date(iso).getTime() - Date.now()) / 86_400_000)
  if (d <= 0) return "today"
  if (d === 1) return "tomorrow"
  return `in ${d} days`
}

/* ── "What did we fix?" — line items in plain words ──────── */

function prettyComponent(code: string): string {
  const s = code.toLowerCase().replace(/_/g, " ")
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function workSummary(e: ApiEstimate): string | null {
  const items = e.lineItems ?? []
  if (items.length === 0) return null
  const first = prettyComponent(items[0].component)
  if (items.length === 1) {
    return `${first} ${items[0].action === "Replace" ? "replaced" : "repaired"}`
  }
  const second = prettyComponent(items[1].component)
  return `${first} · ${second}${items.length > 2 ? ` +${items.length - 2} more` : ""}`
}

/* ── Conversation wrapper ───────────────────────────────── */

function ConversationSection({ children }: { children: React.ReactNode }) {
  return (
    <section>
      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-2 px-1">
        Conversation
      </p>
      {children}
    </section>
  )
}

/* ── Estimates history ──────────────────────────────────── */

function EstimatesHistory({ profile }: { profile: Profile }) {
  if (profile.vehicles.length === 0) return null
  return (
    <section>
      <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-zinc-400 mb-2 px-1">
        Vehicles & estimates
      </p>
      <div className="space-y-4">
        {profile.vehicles.map((vg) => (
          <div
            key={vg.vin ?? vg.label}
            className="rounded-2xl border border-zinc-200/80 bg-white overflow-hidden"
          >
            <div className="px-4 sm:px-5 py-3 border-b border-zinc-100 flex items-center gap-2.5">
              <Car01 className="size-4 text-zinc-400 shrink-0" aria-hidden />
              <p className="text-[13.5px] font-semibold text-zinc-900 truncate">
                {vg.label}
              </p>
              {vg.vin && (
                <code className="text-[11px] font-mono text-zinc-400 truncate hidden sm:inline">
                  {vg.vin}
                </code>
              )}
            </div>
            <div className="divide-y divide-zinc-100">
              {vg.estimates.map((e) => (
                <EstimateRow key={e.id} estimate={e} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function EstimateRow({ estimate }: { estimate: ApiEstimate }) {
  const date = new Date(estimate.createdAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
  return (
    <Link
      to={`/estimates/${estimate.id}`}
      className="flex items-center gap-3 px-4 sm:px-5 py-3 hover:bg-zinc-50/70 transition-colors group"
    >
      <StatusBadge status={estimate.status} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-zinc-800 tabular-nums">
          {formatMoney(estimate.avg)}
        </p>
        {/* The work, not just the number — "what they fixed" per visit. */}
        <p className="text-[11.5px] text-zinc-400 leading-tight truncate">
          {workSummary(estimate) ? `${workSummary(estimate)} · ${date}` : date}
        </p>
      </div>
      <ChevronRight
        className="size-4 text-zinc-300 group-hover:text-zinc-500 group-hover:translate-x-0.5 transition-all shrink-0"
        aria-hidden
      />
    </Link>
  )
}
